import { Plus, Trash2 } from "lucide-react"

import { DepartamentoYCodigo, Texto } from "./campos"
import { ANCHOS_LOMO, LARGO_LOMO } from "./medidas"
import type { DatosRotulo } from "./tipos"

/**
 * El formulario de un rótulo. Un bloque de campos por formato; la compañía se elige
 * arriba, en la pantalla, porque es igual en los cinco.
 *
 * ⚠ **Escribe sobre el objeto entero y lo devuelve entero.** No hay estado local:
 * lo que se teclea sube al padre y baja otra vez ya pintado en la vista previa. Con
 * estado propio habría dos verdades —lo tecleado y lo previsualizado— y la previa
 * enseñaría el fotograma anterior, que en un módulo cuyo único fin es ver antes de
 * imprimir lo deja sin sentido.
 *
 * ⚠ **Los `maxLength` son los del esquema del backend, no adornos.** Son la garantía
 * de que el texto cabe a la medida fija del formato (ver `medidas.ts`). Cortarlo aquí
 * —donde se ve el contador— es mejor que aceptarlo, guardarlo y que salga recortado
 * con puntos suspensivos en la carpeta.
 */
export function EditorRotulo({
  datos,
  onCambio,
}: {
  datos: DatosRotulo
  onCambio: (datos: DatosRotulo) => void
}) {
  switch (datos.tipo) {
    case "PESTANA":
      return (
        <div className="grid gap-3">
          <Texto
            etiqueta="Contenido"
            valor={datos.contenido}
            max={60}
            ayuda="Qué contiene la carpeta: Nómina, Declaraciones de IVA…"
            onCambio={(contenido) => onCambio({ ...datos, contenido })}
          />
          <DepartamentoYCodigo
            departamento={datos.departamento ?? ""}
            codigo={datos.siglas ?? ""}
            onCambio={(departamento, siglas) => onCambio({ ...datos, departamento, siglas })}
          />
          <Texto
            etiqueta="Período"
            valor={datos.periodo ?? ""}
            max={20}
            opcional={true}
            ayuda="2026, Ene–jun…"
            onCambio={(v) => onCambio({ ...datos, periodo: v || null })}
          />
        </div>
      )

    case "ETIQUETA_CARPETA":
      return (
        <div className="grid gap-3">
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <Texto
              etiqueta="Empresa"
              valor={datos.empresa}
              max={120}
              onCambio={(empresa) => onCambio({ ...datos, empresa })}
            />
            <Texto
              etiqueta="Código o siglas"
              valor={datos.siglas}
              max={12}
              ancho="w-28"
              ayuda="FI–012, ICHI…"
              onCambio={(siglas) => onCambio({ ...datos, siglas: siglas.toUpperCase() })}
            />
          </div>
          <Texto
            etiqueta="Nombre del expediente"
            valor={datos.nombre_expediente}
            max={160}
            onCambio={(nombre_expediente) => onCambio({ ...datos, nombre_expediente })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Texto
              etiqueta="Sección de archivo"
              valor={datos.seccion_archivo}
              max={60}
              ayuda="Finanzas, Legal…"
              onCambio={(seccion_archivo) => onCambio({ ...datos, seccion_archivo })}
            />
            <Texto
              etiqueta="Período"
              valor={datos.periodo ?? ""}
              max={40}
              opcional={true}
              onCambio={(v) => onCambio({ ...datos, periodo: v || null })}
            />
          </div>

          <fieldset className="border-0 p-0">
            <legend className="rotulo mb-2 w-full">Contenido</legend>
            <div className="grid gap-2">
              {datos.secciones.map((seccion, indice) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: el índice ES el número de sección impreso; no se reordenan
                  key={indice}
                  className="flex items-center gap-2"
                >
                  <span
                    className="tabular w-20 shrink-0 text-xs"
                    style={{ color: "var(--fg-muted)" }}
                  >
                    N° {indice + 1}
                  </span>
                  <input
                    className="campo h-9 flex-1 text-sm"
                    value={seccion.descripcion}
                    maxLength={120}
                    aria-label={`Sección ${indice + 1}`}
                    onChange={(e) => {
                      const secciones = [...datos.secciones]
                      secciones[indice] = { descripcion: e.target.value }
                      onCambio({ ...datos, secciones })
                    }}
                  />
                  <button
                    type="button"
                    className="boton-secundario h-9 px-2"
                    aria-label={`Quitar la sección ${indice + 1}`}
                    onClick={() =>
                      onCambio({
                        ...datos,
                        secciones: datos.secciones.filter((_, i) => i !== indice),
                      })
                    }
                  >
                    <Trash2 size={14} aria-hidden={true} />
                  </button>
                </div>
              ))}
            </div>
            {/* ⚠ El botón desaparece al llegar al tope en vez de quedarse
                deshabilitado: con ocho secciones ya no cabe una novena en la
                etiqueta, y un botón apagado invita a preguntarse por qué. */}
            {datos.secciones.length < 8 ? (
              <button
                type="button"
                className="boton-secundario mt-2 h-8 px-2 text-xs"
                onClick={() =>
                  onCambio({ ...datos, secciones: [...datos.secciones, { descripcion: "" }] })
                }
              >
                <Plus size={14} aria-hidden={true} />
                Añadir sección
              </button>
            ) : (
              <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
                Ocho secciones es lo que cabe en la etiqueta sin encoger la letra.
              </p>
            )}
          </fieldset>
        </div>
      )

    case "LOMO":
      return (
        <div className="grid gap-3">
          <Texto
            etiqueta="Contenido"
            valor={datos.titulo}
            max={70}
            ayuda={
              datos.orientacion === "HORIZONTAL"
                ? "Se lee de frente, en las líneas que haga falta"
                : "Corre de abajo hacia arriba, como se lee en la estantería"
            }
            onCambio={(titulo) => onCambio({ ...datos, titulo })}
          />
          <fieldset className="grid gap-1">
            <legend className="mb-1 text-xs font-medium">Orientación del contenido</legend>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ["VERTICAL", "Vertical"],
                  ["HORIZONTAL", "Horizontal"],
                ] as const
              ).map(([valor, etiqueta]) => {
                const activa = (datos.orientacion ?? "VERTICAL") === valor
                return (
                  <button
                    key={valor}
                    type="button"
                    aria-pressed={activa}
                    onClick={() => onCambio({ ...datos, orientacion: valor })}
                    className={`foco-cristal h-9 rounded-[var(--radius-control)] border px-3 text-sm ${activa ? "activo-cristal font-medium" : "toque-cristal"}`}
                  >
                    {etiqueta}
                  </button>
                )
              })}
            </div>
          </fieldset>
          <DepartamentoYCodigo
            departamento={datos.subtitulo ?? ""}
            codigo={datos.siglas ?? ""}
            onCambio={(subtitulo, siglas) => onCambio({ ...datos, subtitulo, siglas })}
          />
          <Texto
            etiqueta="Período"
            valor={datos.fecha ?? ""}
            max={40}
            opcional={true}
            ayuda="2026, Enero – junio 2026…"
            onCambio={(v) => onCambio({ ...datos, fecha: v || null })}
          />
          <Texto
            etiqueta="Detalle"
            valor={datos.contenido ?? ""}
            max={120}
            opcional={true}
            ayuda="Una frase pequeña sobre el período"
            onCambio={(v) => onCambio({ ...datos, contenido: v || null })}
          />
          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-1">
              <span className="text-xs font-medium">Ancho del canto (mm)</span>
              <input
                className="campo tabular h-9 text-sm"
                type="number"
                list="anchos-de-lomo"
                min={25}
                max={120}
                value={datos.ancho_mm}
                onChange={(e) => onCambio({ ...datos, ancho_mm: Number(e.target.value) })}
              />
              <datalist id="anchos-de-lomo">
                {ANCHOS_LOMO.map((opcion) => (
                  <option key={opcion.mm} value={opcion.mm}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </datalist>
              <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
                De 25 a 120. Mide el canto con una regla si dudas.
              </span>
            </label>
            <label className="grid gap-1">
              <span className="text-xs font-medium">Largo (mm)</span>
              <input
                className="campo tabular h-9 text-sm"
                type="number"
                min={LARGO_LOMO.min}
                max={LARGO_LOMO.max}
                value={datos.alto_mm}
                onChange={(e) => onCambio({ ...datos, alto_mm: Number(e.target.value) })}
              />
              <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
                De {LARGO_LOMO.min} a {LARGO_LOMO.max}: lo que cabe en una hoja carta.
              </span>
            </label>
          </div>
        </div>
      )

    case "CARATULA":
      return (
        <div className="grid gap-3">
          <Texto
            etiqueta="Título"
            valor={datos.titulo}
            max={160}
            onCambio={(titulo) => onCambio({ ...datos, titulo })}
          />
          <DepartamentoYCodigo
            departamento={datos.departamento ?? ""}
            codigo={datos.codigo ?? ""}
            onCambio={(departamento, codigo) => onCambio({ ...datos, departamento, codigo })}
          />
          <Texto
            etiqueta="Período"
            valor={datos.periodo ?? ""}
            max={60}
            opcional={true}
            ayuda="2026, Enero – junio 2026…"
            onCambio={(v) => onCambio({ ...datos, periodo: v || null })}
          />
          <Texto
            etiqueta="Subtítulo"
            valor={datos.subtitulo ?? ""}
            max={160}
            opcional={true}
            onCambio={(v) => onCambio({ ...datos, subtitulo: v || null })}
          />
          <div className="grid grid-cols-3 gap-3">
            <Texto
              etiqueta="Sección"
              valor={datos.seccion ?? ""}
              max={40}
              opcional={true}
              onCambio={(v) => onCambio({ ...datos, seccion: v || null })}
            />
            <Texto
              etiqueta="Responsable"
              valor={datos.responsable ?? ""}
              max={60}
              opcional={true}
              onCambio={(v) => onCambio({ ...datos, responsable: v || null })}
            />
            <Texto
              etiqueta="Ubicación"
              valor={datos.ubicacion ?? ""}
              max={40}
              opcional={true}
              onCambio={(v) => onCambio({ ...datos, ubicacion: v || null })}
            />
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <Texto
              etiqueta="Entidad"
              valor={datos.entidad ?? ""}
              max={160}
              opcional={true}
              ayuda="De quién es lo archivado, si es de un cliente"
              onCambio={(v) => onCambio({ ...datos, entidad: v || null })}
            />
            <Texto
              etiqueta="RIF"
              valor={datos.rif ?? ""}
              max={20}
              opcional={true}
              ancho="w-36"
              onCambio={(v) => onCambio({ ...datos, rif: v || null })}
            />
          </div>
          <label className="grid gap-1">
            <span className="text-xs font-medium">Hoja</span>
            <select
              className="campo h-9 text-sm"
              value={datos.hoja}
              onChange={(e) => onCambio({ ...datos, hoja: e.target.value as "CARTA" | "OFICIO" })}
            >
              <option value="CARTA">Carta · 216 × 279 mm</option>
              <option value="OFICIO">Oficio · 216 × 356 mm</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={datos.con_membrete}
              onChange={(e) => onCambio({ ...datos, con_membrete: e.target.checked })}
            />
            Llevar la razón social de la compañía al pie
          </label>
        </div>
      )

    case "TEJUELO":
      return (
        <div className="grid gap-3">
          <Texto
            etiqueta="Libro"
            valor={datos.libro}
            max={80}
            ayuda="Libro de actas, Libro de accionistas…"
            onCambio={(libro) => onCambio({ ...datos, libro })}
          />
          <Texto
            etiqueta="Número y código"
            valor={datos.codigo ?? ""}
            max={40}
            opcional={true}
            ayuda="Libro N.º 3 · AD–001"
            onCambio={(v) => onCambio({ ...datos, codigo: v || null })}
          />
          <Texto
            etiqueta="Detalle"
            valor={datos.detalle ?? ""}
            max={80}
            opcional={true}
            ayuda="Junta de socios · 2024 – 2026"
            onCambio={(v) => onCambio({ ...datos, detalle: v || null })}
          />
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <Texto
              etiqueta="Empresa dueña del libro"
              valor={datos.empresa}
              max={120}
              onCambio={(empresa) => onCambio({ ...datos, empresa })}
            />
            <Texto
              etiqueta="RIF"
              valor={datos.rif ?? ""}
              max={20}
              opcional={true}
              ancho="w-36"
              onCambio={(v) => onCambio({ ...datos, rif: v || null })}
            />
          </div>
        </div>
      )
  }
}
